'use client';

import React, { useCallback, useMemo } from 'react';
import { useGlobalClientContext } from '../common/GlobalClientContext';
import { Input } from '@altus-ui/components_v2/v3/Input';
import InfoOutlineIcon from '../icons/Info.outline';
import {
  RadioButton,
  RadioGroup
} from '@altus-ui/components_v2/v3/RadioButton';
import { GpuClassType } from './type';
import { RequiredError } from './interestError';
import { Checkbox, CHECKBOX_STATE } from '@altus-ui/components_v2/v3/Checkbox';

interface InterestGpuClass {
  validating: boolean;
  disabled: boolean;
  gpuClassType: GpuClassType;
  setGpuClassType: (value: GpuClassType) => void;
  gpuClassSelect: string[];
  setGpuClassSelect: (value: string[]) => void;
  gpuClassInput: string;
  setGpuClassInput: (value: string) => void;
}

export default function InterestGpuClass({
  validating,
  disabled,
  gpuClassType,
  setGpuClassType,
  gpuClassSelect,
  setGpuClassSelect,
  gpuClassInput,
  setGpuClassInput
}: InterestGpuClass) {
  const {
    i18nHelper: { t }
  } = useGlobalClientContext();

  const GPU_CLASSES = ['L40S', 'H200', 'B200', 'GB200', 'B300', 'GB300'];

  const handleCheckboxChange = useCallback(
    (gpuClass: string) => {
      if (gpuClassSelect.includes(gpuClass)) {
        setGpuClassSelect(gpuClassSelect.filter((item) => item !== gpuClass));
      } else {
        setGpuClassSelect([...gpuClassSelect, gpuClass]);
      }
    },
    [gpuClassSelect]
  );

  const missingSelect = useMemo(
    () =>
      validating &&
      gpuClassType === 'GPU_CLASS_SELECT' &&
      gpuClassSelect.length === 0,

    [validating, gpuClassType, gpuClassSelect]
  );

  const missingInput = useMemo(
    () =>
      validating &&
      gpuClassType === 'GPU_CLASS_INPUT' &&
      gpuClassInput.trim().length === 0,
    [validating, gpuClassType, gpuClassInput]
  );

  return (
    <RadioGroup
      value={gpuClassType}
      onValueChange={(value) => setGpuClassType(value as GpuClassType)}
      className="flex flex-col w-full flex-1 gap-s">
      <div className="flex flex-row items-start w-full gap-2xs">
        <RadioButton value="GPU_CLASS_SELECT" disabled={disabled} />
        <div className="flex flex-col gap-s pb-s w-full border-b border-b-gray-100 border-solid">
          <span
            className="font-body font-medium text-sm leading-2xs text-primary hover:cursor-pointer"
            onClick={() => setGpuClassType('GPU_CLASS_SELECT')}>
            {t('interest.gpu_preference.select', {})}
          </span>
          {gpuClassType === 'GPU_CLASS_SELECT' && (
            <div className="flex flex-row items-center gap-s">
              {GPU_CLASSES.map((gpuClass) => (
                <div className="flex flex-row items-center flex-shrink-0 gap-2xs" key={gpuClass}>
                  <Checkbox
                    onCheckedChange={(_value) => {
                      handleCheckboxChange(gpuClass);
                    }}
                    checked={gpuClassSelect.includes(gpuClass)}
                    id={`gpu-class-${gpuClass}`}
                    state={
                      missingSelect
                        ? CHECKBOX_STATE.DESTRUCTIVE
                        : CHECKBOX_STATE.DEFAULT
                    }
                    disabled={disabled}
                  />
                  <div
                    className={`font-body font-regular text-sm leading-md text-primary hover:cursor-pointer ${missingSelect ? 'text-negative' : ''}`}
                    onClick={() => {
                      handleCheckboxChange(gpuClass);
                    }}>
                    {gpuClass}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-row items-start w-full gap-2xs">
        <RadioButton value="GPU_CLASS_INPUT" disabled={disabled} />
        <div
          className="flex flex-col w-full gap-s hover:cursor-pointer"
          onClick={() => setGpuClassType('GPU_CLASS_INPUT')}>
          <span className="font-body font-medium text-sm leading-2xs text-primary">
            {t('interest.gpu_preference.input', {})}
          </span>
          {gpuClassType === 'GPU_CLASS_INPUT' && (
            <div className="flex flex-col gap-3xs cursor-default">
              <Input
                value={gpuClassInput}
                onValueChange={(value: string) => {
                  setGpuClassInput(value);
                }}
                placeholder={t('interest.gpu_preference.input.placeholder', {})}
                error={missingInput}
                disabled={disabled}
                showLabel={false}
                showHintText={false}
              />
              <div className="flex flex-row items-center gap-3xs">
                <InfoOutlineIcon />
                <span className="font-caption font-regular text-xs leading-xs text-secondary">
                  {t('interest.gpu_preference.input.hint', {})}
                </span>
              </div>
              {missingInput && <RequiredError />}
            </div>
          )}
        </div>
      </div>
    </RadioGroup>
  );
}
